/**
 * Bank Account API Service
 *
 * Provides API methods for managing gaushala bank account details
 * Endpoints: /api/v1/gaushala/bank-accounts
 */

/**
 * Bank account details response structure
 */
export interface BankAccountResponse {
  id: number;
  gaushalaId: number;
  accountHolderName: string;
  maskedAccountNumber: string;
  ifscCode: string;
  bankName: string;
  branchName?: string;
  accountType?: string;
  email?: string;
  phone?: string;
  isActive: boolean;
  isVerified: boolean;
  verifiedAt?: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Bank account create request structure
 */
export interface CreateBankAccountRequest {
  accountHolderName: string;
  accountNumber: string;
  ifscCode: string;
  bankName: string;
  branchName?: string;
  accountType?: string;
  email?: string;
  phone?: string;
}

/**
 * Bank account update request structure
 */
export interface UpdateBankAccountRequest {
  accountHolderName?: string;
  accountNumber?: string;
  ifscCode?: string;
  bankName?: string;
  branchName?: string;
  accountType?: string;
  email?: string;
  phone?: string;
}

/**
 * API Response wrapper
 */
interface ApiResponse<T> {
  message: string;
  data?: T;
  success?: boolean;
}

/**
 * Get the gaushala service base URL
 * Uses relative path in dev mode for Vite proxy
 */
function getGaushalaServiceUrl(): string {
  const isDevelopment = import.meta.env.DEV;
  return isDevelopment
    ? '/gaushala-service'
    : (import.meta.env.VITE_GAUSHALA_SERVICE_URL || 'http://localhost:8086/gaushala-service');
}

/**
 * Get authentication token from localStorage
 */
function getAuthToken(): string | null {
  const token = localStorage.getItem('saubhagya_jwt_token') || localStorage.getItem('token');
  if (token) {
    console.log('[BankAccountService] Token retrieved from storage:', {
      type: typeof token,
      length: token.length,
      startsWithBearer: token.startsWith('Bearer '),
      first30Chars: token.substring(0, 30),
      hasComma: token.includes(','),
    });
  }
  return token;
}

/**
 * Create headers with authentication
 */
function createHeaders(): HeadersInit {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };

  const token = getAuthToken();
  if (token) {
    // Ensure we only send the raw JWT token, not with Bearer prefix
    const cleanToken = token.startsWith('Bearer ') ? token.substring(7) : token;
    headers['Authorization'] = `Bearer ${cleanToken}`;
    console.log('[BankAccountService] Authorization header created:', {
      tokenLength: cleanToken.length,
      headerStart: (headers['Authorization'] as string).substring(0, 30),
    });
  } else {
    console.warn('No authentication token found for bank account service');
  }

  return headers;
}

/**
 * Bank Account API service
 */
export const bankAccountService = {
  /**
   * Get bank account details for current user's gaushala
   * GET /api/v1/gaushala/bank-accounts
   *
   * @returns Promise<BankAccountResponse> - Bank account details
   * @throws Error if the API call fails
   */
  async getBankAccount(): Promise<BankAccountResponse> {
    const baseUrl = getGaushalaServiceUrl();
    const url = `${baseUrl}/api/v1/gaushala/bank-accounts`;

    try {
      console.log(`[BankAccountService] Fetching bank account from: ${url}`);
      const headers = createHeaders();
      console.log(`[BankAccountService] Headers:`, { 'Content-Type': headers['Content-Type'], hasAuth: !!headers['Authorization'] });

      const response = await fetch(url, {
        method: 'GET',
        headers: headers,
      });

      console.log(`[BankAccountService] Response status: ${response.status} ${response.statusText}`);

      if (!response.ok) {
        try {
          const errorData = await response.json();
          const errorMessage = errorData.message || `Failed to fetch bank account: ${response.status} ${response.statusText}`;
          console.error(`[BankAccountService] API Error: ${errorMessage}`);
          throw new Error(errorMessage);
        } catch (parseError) {
          const errorMessage = `Failed to fetch bank account: ${response.status} ${response.statusText}`;
          console.error(`[BankAccountService] Parse Error: ${errorMessage}`, parseError);
          throw new Error(errorMessage);
        }
      }

      const data = await response.json();
      console.log(`[BankAccountService] Bank account fetched successfully`, data);

      // Extract bank account from response
      if (data.data) {
        return data.data as BankAccountResponse;
      }

      return data as BankAccountResponse;
    } catch (error) {
      console.error('[BankAccountService] Error fetching bank account:', error);
      throw error;
    }
  },

  /**
   * Create a new bank account for current user's gaushala
   * POST /api/v1/gaushala/bank-accounts
   *
   * @param request - CreateBankAccountRequest with account details
   * @returns Promise<BankAccountResponse> - Created bank account details
   * @throws Error if the API call fails
   */
  async createBankAccount(
    request: CreateBankAccountRequest
  ): Promise<BankAccountResponse> {
    const baseUrl = getGaushalaServiceUrl();
    const url = `${baseUrl}/api/v1/gaushala/bank-accounts`;

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: createHeaders(),
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        const errorData = await response.json();
        const errorMessage = errorData.message || 'Failed to create bank account';
        throw new Error(errorMessage);
      }

      const data = await response.json();

      // Extract bank account from response
      if (data.data) {
        return data.data as BankAccountResponse;
      }

      return data as BankAccountResponse;
    } catch (error) {
      console.error('Error creating bank account:', error);
      throw error;
    }
  },

  /**
   * Update existing bank account for current user's gaushala
   * PUT /api/v1/gaushala/bank-accounts
   *
   * @param request - UpdateBankAccountRequest with fields to update
   * @returns Promise<BankAccountResponse> - Updated bank account details
   * @throws Error if the API call fails
   */
  async updateBankAccount(
    request: UpdateBankAccountRequest
  ): Promise<BankAccountResponse> {
    const baseUrl = getGaushalaServiceUrl();
    const url = `${baseUrl}/api/v1/gaushala/bank-accounts`;

    try {
      const response = await fetch(url, {
        method: 'PUT',
        headers: createHeaders(),
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        const errorData = await response.json();
        const errorMessage = errorData.message || 'Failed to update bank account';
        throw new Error(errorMessage);
      }

      const data = await response.json();

      // Extract bank account from response
      if (data.data) {
        return data.data as BankAccountResponse;
      }

      return data as BankAccountResponse;
    } catch (error) {
      console.error('Error updating bank account:', error);
      throw error;
    }
  },

  /**
   * Check if gaushala has a verified bank account
   * Attempts to fetch and checks verification status
   *
   * @returns Promise<boolean> - True if verified account exists
   */
  async hasVerifiedBankAccount(): Promise<boolean> {
    try {
      const bankAccount = await this.getBankAccount();
      return bankAccount.isVerified === true;
    } catch {
      return false;
    }
  },
};
